import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  GET_INTEGRATION_LINK_SESSION_INFO,
  type GetIntegrationLinkedSessionInfoResponse,
} from '@heidi/integrations/ehr/contracts';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';

/**
 * Imperative sibling of `useSessionEhrProvider` for non-React call sites (note
 * generation, document create/regenerate) that need the ACTIVE session's EHR
 * provider without a hook.
 *
 * Reads the query cache with the same three-tier precedence as the hook:
 *   1. Linked appointment provider — most authoritative; reflects the
 *      integration actually used to book this appointment.
 *   2. session.ehr_provider — set by the backend at session creation.
 *   3. patient_profile.ehr — legacy patient EHR profile; last resort (may be stale).
 *
 * Taking the link tier from cache means a stale session field can't out-vote a
 * linked appointment. Returns undefined when nothing is cached so callers fall
 * back to their own default (e.g. the first-connected onboarding session).
 */
export function resolveActiveSessionProvider(sessionId: string): EHRProvider | undefined {
  const linkedAppointmentProvider =
    reactQueryClient.getQueryData<GetIntegrationLinkedSessionInfoResponse | null>([
      GET_INTEGRATION_LINK_SESSION_INFO,
      { sessionId },
    ])?.provider_appointment_details?.provider as EHRProvider | undefined;

  const session = reactQueryClient.getQueryData<QuerySessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId },
  ]);

  return (
    linkedAppointmentProvider ??
    (session?.ehr_provider as EHRProvider | undefined) ??
    (session?.patient_profile?.ehr as EHRProvider | undefined)
  );
}
